
interface Listing {
  id: string;
  description: string;
  mrr: number;
  totalRevenue: number;
  cashFlow: number;
  location: string;
  niches: string[];
  employeeCount: number;
  askingPrice: number;
  type: 'agency' | 'saas' | 'ecommerce';
  featured: boolean;
}

interface ListingCardProps {
  listing: Listing;
  onInquiry: () => void;
}

export default function ListingCard({ listing, onInquiry }: ListingCardProps) {
  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount.toLocaleString()}`;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'agency': return 'ri-team-line';
      case 'saas': return 'ri-cloud-line';
      case 'ecommerce': return 'ri-shopping-cart-line';
      default: return 'ri-building-line';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'agency': return 'text-blue-400 bg-blue-500/20';
      case 'saas': return 'text-green-400 bg-green-500/20';
      case 'ecommerce': return 'text-purple-400 bg-purple-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  return (
    <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 group">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-xl ${getTypeColor(listing.type)} flex items-center justify-center`}>
            <i className={`${getTypeIcon(listing.type)} text-lg`}></i>
          </div>
          <div>
            <div className="text-sm text-gray-400 capitalize">{listing.type}</div>
            {listing.featured && (
              <div className="flex items-center text-xs text-yellow-400 mt-1">
                <i className="ri-star-fill mr-1"></i>
                Featured
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-400">Asking Price</div>
          <div className="text-lg font-semibold text-white">{formatCurrency(listing.askingPrice)}</div>
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-300 text-sm leading-relaxed mb-6 line-clamp-3">
        {listing.description}
      </p>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-black/30 rounded-xl p-3">
          <div className="text-xs text-gray-400 mb-1">MRR</div>
          <div className="text-lg font-semibold text-green-400">{formatCurrency(listing.mrr)}</div>
        </div>
        <div className="bg-black/30 rounded-xl p-3">
          <div className="text-xs text-gray-400 mb-1">Annual Revenue</div>
          <div className="text-lg font-semibold text-blue-400">{formatCurrency(listing.totalRevenue)}</div>
        </div>
        <div className="bg-black/30 rounded-xl p-3">
          <div className="text-xs text-gray-400 mb-1">Cash Flow</div>
          <div className="text-lg font-semibold text-purple-400">{formatCurrency(listing.cashFlow)}</div>
        </div>
        <div className="bg-black/30 rounded-xl p-3">
          <div className="text-xs text-gray-400 mb-1">Team Size</div>
          <div className="text-lg font-semibold text-orange-400">{listing.employeeCount} people</div>
        </div>
      </div>

      {/* Location & Niches */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center text-sm text-gray-400">
          <i className="ri-map-pin-line mr-2"></i>
          {listing.location}
        </div>
        <div className="flex flex-wrap gap-2">
          {listing.niches.slice(0, 3).map((niche, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-white/10 text-gray-300 text-xs rounded-lg"
            >
              {niche}
            </span>
          ))}
          {listing.niches.length > 3 && (
            <span className="px-2 py-1 bg-white/10 text-gray-400 text-xs rounded-lg">
              +{listing.niches.length - 3} more
            </span>
          )}
        </div>
      </div>

      {/* Action Button */}
      <button
        onClick={onInquiry}
        className="w-full bg-white text-black font-medium py-3 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap group-hover:bg-blue-500 group-hover:text-white"
      >
        <i className="ri-mail-line mr-2"></i>
        Request Information
      </button>
    </div>
  );
}
